import React, { useState, useEffect, useRef, useCallback } from 'react';
import CustomerApi from './customerApi';
import checkAuthApi from '../lib/checkAuthApi'
import { Customer } from './customerType';
import HorizontalMenu from '../components/common/horizontalMenu';
import { FilePlus } from 'lucide-react';
import { GoogleMap, LoadScript, Marker, Autocomplete, Libraries } from '@react-google-maps/api';
import { validateEmail, validateAndFormatPhoneNumber } from '../lib/validator';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const CreateCustomerPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Customer', path: '/main/customer' },
    { label: 'Create', path: '/main/customer/create' },
  ];

  const libraries = useRef<Libraries>(['places']);
  const [customer, setCustomer] = useState<Partial<Customer>>({
    first_name: '',
    last_name: '',
    customer_company_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip: '',
    latitude: 0,
    longitude: 0,
  });

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [mapCenter, setMapCenter] = useState({ lat: 28.0517, lng: -82.4588 });
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [consentPhone, setConsentPhone] = useState(false);
  const [consentEmail, setConsentEmail] = useState(false);
  const [searchText, setSearchText] = useState('');
  const areAllCheckboxesChecked = consentPhone && consentEmail;

  // Use ref to always have access to latest customer state
  const customerRef = useRef(customer);
  useEffect(() => {
    customerRef.current = customer;
  }, [customer]);

  useEffect(() => {
    const checkAuth = async () => {
      setErrorMessage('');
      setMessage('');
      try {
        await checkAuthApi.checkAuth();
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };
    checkAuth();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCustomer({ ...customer, [name]: value });
  };

  // Handle search text change (captures browser autofill)
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  // Geocode address from text (for browser autofill)
const geocodeAddress = useCallback(async (address: string) => {
  if (!address || !window.google) return;

  try {
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({ address });
    
    if (result.results && result.results[0]) {
      const place = result.results[0];
      
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });

        let address1 = '';
        let city = '';
        let state = '';
        let zip = '';

        if (place.address_components) {
          for (const component of place.address_components) {
            const componentType = component.types[0];
            
            switch (componentType) {
              case 'street_number':
                address1 = `${component.long_name} ${address1}`;
                break;
              case 'route':
                address1 += component.short_name;
                break;
              case 'locality':
              case 'sublocality_level_1':
              case 'administrative_area_level_3':
                city = component.long_name;
                break;
              case 'administrative_area_level_1':
                state = component.long_name;
                break;
              case 'postal_code':
                zip = component.long_name;
                break;
            }
          }
        }

        setCustomer(prev => ({
          ...prev,
          address_1: address1.trim(),
          city: city,
          state: state,
          zip: zip,
          latitude: lat,
          longitude: lng,
        }));
      }
    }
  } catch (error) {
    console.error('Geocoding failed:', error);
  }
}, []);

  // Handle blur on search input (geocode if filled by browser)
  const handleSearchBlur = () => {
    if (searchText && !customer.latitude) {
      // If there's text but no coordinates, it was probably browser autofill
      geocodeAddress(searchText);
    }
  };

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete(autocomplete);
  }, []);

  
const onPlaceChanged = useCallback(() => {
  if (autocomplete !== null) {
    const place = autocomplete.getPlace();
    
    if (place.geometry && place.geometry.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMapCenter({ lat, lng });

      let address1 = '';
      let city = '';
      let state = '';
      let zip = '';

      if (place.address_components) {
        for (const component of place.address_components) {
          const componentType = component.types[0];
          
          switch (componentType) {
            case 'street_number':
              address1 = `${component.long_name} ${address1}`;
              break;
            case 'route':
              address1 += component.short_name;
              break;
            case 'locality':
            case 'sublocality_level_1':
            case 'administrative_area_level_3':
              city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.long_name;
              break;
            case 'postal_code':
              zip = component.long_name;
              break;
          }
        }
      }

      const newCustomer = {
        ...customerRef.current,
        address_1: address1.trim(),
        city: city,
        state: state,
        zip: zip,
        latitude: lat,
        longitude: lng,
      };
      
      setCustomer(newCustomer);
      setSearchText(place.formatted_address || '');
    }
  }
}, [autocomplete]);


  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'phone':
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setCustomer(prev => ({ ...prev, phone: phoneResult.value }));
        }
        break;

      case 'email':
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setCustomer(prev => ({ ...prev, email: emailResult.value }));
        }
        break;
    }
    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  };

  const validateForm = (): boolean => {
    setErrorMessage('');

    const phoneResult = validateAndFormatPhoneNumber(customer.phone || '');
    if (!phoneResult.isValid) {
      setErrorMessage(phoneResult.value);
      return false;
    }

    const emailResult = validateEmail(customer.email || '');
    if (!emailResult.isValid) {
      setErrorMessage(emailResult.error || 'Invalid email address');
      return false;
    }


    if (!customer.city || !customer.city.trim()) {
      setErrorMessage('City is required. Please select a valid address from the suggestions.');
      return false;
    }

    if (!customer.state || !customer.state.trim()) {
      setErrorMessage('State is required. Please select a valid address from the suggestions.');
      return false;
    }

    if (!consentPhone) {
      setErrorMessage('You must consent to the use of your phone number for communication.');
      return false;
    }

    if (!consentEmail) {
      setErrorMessage('You must consent to the use of your email for communication.');
      return false;
    }

    return true;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (errorMessage) {
      return;
    }

    if (!consentPhone || !consentEmail) {
      setErrorMessage('You must accept all the terms and consents to continue.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      await CustomerApi.create(customer as Customer);
      setMessage('Customer created successfully');
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    }
  };

  const handleConsentPhoneChange = (checked: boolean) => {
    setConsentPhone(checked);
  };

  const handleConsentEmailChange = (checked: boolean) => {
    setConsentEmail(checked);
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">Create Customer</h1>

          {message ? (
            <Alert variant="success">
              {message}
            </Alert>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && (
                <Alert variant="error">
                  {errorMessage}
                </Alert>
              )}

              <Alert variant="info">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consentPhone"
                      checked={consentPhone}
                      onCheckedChange={handleConsentPhoneChange}
                    />
                    <Label htmlFor="consentPhone" className="cursor-pointer font-normal">
                      I confirm that the customer has agreed to receive SMS messages at the provided phone number for project-related estimates, invoices, and other communications
                    </Label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="consentEmail"
                      checked={consentEmail}
                      onCheckedChange={handleConsentEmailChange}
                    />
                    <Label htmlFor="consentEmail" className="cursor-pointer font-normal">
                      I confirm that the customer has agreed to receive email messages at the provided email address for project-related estimates, invoices, and other communications
                    </Label>
                  </div>
                </div>
              </Alert>

              <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <h2 className="form-section-heading">Customer Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="first_name"
                      name="first_name"
                      autoComplete="given-name"
                      value={customer.first_name || ''}
                      maxLength={50}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="last_name">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      id="last_name"
                      name="last_name"
                      autoComplete="family-name"
                      value={customer.last_name || ''}
                      maxLength={50}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="customer_company_name">Company Name</Label>
                  <Input
                    type="text"
                    id="customer_company_name"
                    name="customer_company_name"
                    autoComplete="organization"
                    value={customer.customer_company_name || ''}
                    maxLength={50}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={customer.email || ''}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">
                    Phone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="phone"
                    name="phone"
                    autoComplete="tel"
                    value={customer.phone || ''}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                  />
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <h2 className="form-section-heading">Customer Address</h2>

                <div>
                  <Label htmlFor="address">
                    Type Your Customer Address <span className="text-red-500">*</span>
                  </Label>
                  <LoadScript
                    googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
                    libraries={libraries.current}
                  >
                    <Autocomplete
                      onLoad={onLoad}
                      onPlaceChanged={onPlaceChanged}
                      options={{
                        types: ['address'],
                        componentRestrictions: { country: 'us' },
                      }}
                    >
                      <input
                        type="text"
                        name="address"
                        autoComplete="street-address"
                        value={searchText}
                        onChange={handleSearchChange}
                        onBlur={handleSearchBlur}
                        placeholder="Search for a location"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                      />
                    </Autocomplete>

                    <div className="mt-6">
                      <GoogleMap
                        center={mapCenter}
                        zoom={11}
                        mapContainerStyle={{ height: '400px', width: '100%', borderRadius: '8px' }}
                      >
                        <Marker position={mapCenter} />
                      </GoogleMap>
                    </div>
                  </LoadScript>
                </div>

                <div className="pt-4">
                  <Button
                    type="submit"
                    disabled={!areAllCheckboxesChecked}
                    size="medium"
                    variant="default"
                  >
                    <FilePlus className="size-5" />
                    Create Customer
                  </Button>
                  {!areAllCheckboxesChecked && (
                    <Alert variant="warning">
                      You must accept both consent checkboxes to continue.
                    </Alert>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default CreateCustomerPage;